// crewbit-admin/src/components/money/GenerateSummaryPanel.tsx
//
// Slide-over rich-jsonb render of `generate_tax_summary` envelope. Per
// Decision §Tax.D8 the server returns the rich payload and the client
// chrome-renders into a print-friendly view.
//
// Hooks: useTaxSummaryDetail({ periodStart, periodEnd, taxType: 'hst' }) on
// mount. Subsequent mounts hit the hook's cached state — refetches only on
// MONEY_MUTATION_EVENT or 30s focus throttle.
//
// CTAs:
//   - Print           → window.print() (uses browser print dialog +
//                       @media print stylesheet inherited from app shell).
//   - Mark as filed   → caller transitions URL state to ?action=mark-filed
//                       (panel handoff via onMarkFiled callback).
//   - Email accountant → deferred per §Tax.PdfExport.
//
// Spec §Tax.7.2 + §Tax.D8.

import { useTaxSummaryDetail } from '../../hooks/useTaxSummaryDetail';
import { formatMoney } from '../../lib/money';
import { palette, admin, radii, spacing } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import type { TaxType } from '../../lib/taxMutations';

interface GenerateSummaryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  periodStart: string | null;
  periodEnd: string | null;
  periodLabel: string;
  taxType?: TaxType;
  onMarkFiled: () => void;
}

const sectionStyle: React.CSSProperties = {
  background: palette.white,
  border: `1px solid ${palette.g150}`,
  borderRadius: radii.md,
  padding: spacing.md,
  marginBottom: spacing.md,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: spacing.sm,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing.sm,
  padding: `${spacing.sm}px 0`,
  borderTop: `1px solid ${palette.g100}`,
};

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${s.toLocaleDateString('en-CA', opts)} – ${e.toLocaleDateString('en-CA', opts)}`;
}

export function GenerateSummaryPanel({
  isOpen,
  onClose,
  periodStart,
  periodEnd,
  periodLabel,
  taxType = 'hst',
  onMarkFiled,
}: GenerateSummaryPanelProps) {
  const { data, isLoading, error } = useTaxSummaryDetail({
    periodStart: isOpen ? periodStart : null,
    periodEnd: isOpen ? periodEnd : null,
    taxType,
  });

  const isQuickMethod = data?.tax_mode === 'quick_method';

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Tax summary"
      subtitle={`${taxType.toUpperCase()} · ${periodLabel}`}
      width={720}
    >
      {isLoading && !data && (
        <div style={{ padding: spacing.lg, color: palette.g500, fontSize: 13 }}>
          Loading summary…
        </div>
      )}

      {error && !data && (
        <div
          style={{
            padding: spacing.md,
            background: palette.redWash,
            border: `1px solid ${palette.red}`,
            borderRadius: radii.sm,
            fontSize: 13,
            color: palette.red,
          }}
        >
          Couldn't load the summary. Try refreshing.
        </div>
      )}

      {data && (
        <div>
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Period</div>
            <div style={{ fontSize: 13.5, color: admin.header, fontVariantNumeric: 'tabular-nums' }}>
              {formatDateRange(data.period_start, data.period_end)}
            </div>
            <div style={{ fontSize: 12, color: palette.g500, marginTop: 4 }}>
              Generated {new Date(data.generated_at).toLocaleString('en-CA')} ·
              Status: <strong style={{ color: palette.g700 }}>{data.status}</strong>
              {data.business_number && (
                <> · BN <span style={{ fontVariantNumeric: 'tabular-nums' }}>{data.business_number}</span></>
              )}
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              {isQuickMethod ? 'Quick method' : 'Boxes'}
            </div>
            {isQuickMethod ? (
              <>
                <SummaryRow label="Box 105 (collected)" value={formatMoney(data.box_105_hst_collected ?? 0)} first />
                <SummaryRow
                  label="Quick method rate"
                  value={`${((data.quick_method_remittance_rate ?? 0) * 100).toFixed(2)}%`}
                />
                <SummaryRow label="Quick method credit" value={formatMoney(data.quick_method_credit ?? 0)} />
                <SummaryRow label="Net (Box 109)" value={formatMoney(data.box_109_net ?? 0)} emphasis />
              </>
            ) : (
              <>
                <SummaryRow label="Box 101 — Revenue" value={formatMoney(data.box_101_revenue ?? 0)} first />
                <SummaryRow label="Box 105 — HST collected" value={formatMoney(data.box_105_hst_collected ?? 0)} />
                <SummaryRow label="Box 108 — ITCs" value={formatMoney(data.box_108_itcs ?? 0)} />
                <SummaryRow label="Box 109 — Net" value={formatMoney(data.box_109_net ?? 0)} emphasis />
              </>
            )}
          </div>

          {data.invoice_contributions && data.invoice_contributions.length > 0 && (
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>
                Invoices ({data.invoice_contributions.length})
              </div>
              {data.invoice_contributions.slice(0, 25).map((inv, idx) => (
                <div key={inv.invoice_id} style={{ ...rowStyle, borderTop: idx === 0 ? 'none' : rowStyle.borderTop }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: admin.header, fontWeight: 500 }}>
                      {inv.invoice_number} · {inv.client_name ?? '—'}
                    </div>
                    <div style={{ fontSize: 11.5, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
                      Paid {new Date(inv.paid_at).toLocaleDateString('en-CA')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: admin.header, fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(inv.total)}
                    </div>
                    <div style={{ fontSize: 11.5, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
                      tax {formatMoney(inv.tax_amount)}
                    </div>
                  </div>
                </div>
              ))}
              {data.invoice_contributions.length > 25 && (
                <div style={{ marginTop: spacing.sm, fontSize: 12, color: palette.g500 }}>
                  + {data.invoice_contributions.length - 25} more — printable export shows all.
                </div>
              )}
            </div>
          )}

          {data.expense_contributions && data.expense_contributions.length > 0 && (
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>
                Expenses ({data.expense_contributions.length})
              </div>
              {data.expense_contributions.slice(0, 25).map((ex, idx) => (
                <div key={ex.expense_id} style={{ ...rowStyle, borderTop: idx === 0 ? 'none' : rowStyle.borderTop }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: admin.header, fontWeight: 500 }}>
                      {ex.description ?? ex.vendor_name ?? '—'}
                    </div>
                    <div style={{ fontSize: 11.5, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
                      {new Date(ex.date).toLocaleDateString('en-CA')}
                      {ex.vendor_name && ex.description ? ` · ${ex.vendor_name}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: admin.header, fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(ex.amount)}
                    </div>
                    <div style={{ fontSize: 11.5, color: palette.g500, fontVariantNumeric: 'tabular-nums' }}>
                      tax {formatMoney(ex.tax_amount)}
                    </div>
                  </div>
                </div>
              ))}
              {data.expense_contributions.length > 25 && (
                <div style={{ marginTop: spacing.sm, fontSize: 12, color: palette.g500 }}>
                  + {data.expense_contributions.length - 25} more — printable export shows all.
                </div>
              )}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing.sm,
              marginTop: spacing.md,
              paddingTop: spacing.md,
              borderTop: `1px solid ${palette.g100}`,
            }}
          >
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                padding: '9px 16px',
                borderRadius: radii.sm,
                border: `1px solid ${palette.g200}`,
                background: palette.white,
                color: palette.g700,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Print
            </button>
            <button
              type="button"
              onClick={onMarkFiled}
              style={{
                padding: '9px 18px',
                borderRadius: radii.sm,
                border: 'none',
                background: palette.accent,
                color: palette.white,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Mark as filed
            </button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  emphasis?: boolean;
  first?: boolean;
}

function SummaryRow({ label, value, emphasis = false, first = false }: SummaryRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.sm,
        padding: `${spacing.sm}px 0`,
        borderTop: first ? 'none' : `1px solid ${palette.g100}`,
      }}
    >
      <div
        style={{
          fontSize: emphasis ? 14 : 13,
          fontWeight: emphasis ? 600 : 500,
          color: emphasis ? admin.header : palette.g700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: emphasis ? 16 : 13.5,
          fontWeight: emphasis ? 700 : 600,
          color: emphasis ? palette.accent : admin.header,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}
